import React from 'react';
import { Button, Card, Divider, Space, Tag, Typography, message } from 'antd';
import { CopyOutlined, DownloadOutlined, MailOutlined, QrcodeOutlined, ReloadOutlined } from '@ant-design/icons';
import { formatDateTime } from '../../../../utils';
import { IMovementRS } from '../../../../models/salesModel';
import {
  getCopyCodeButtonLabel,
  getElectronicCodeLabel,
  getElectronicDocumentTitle,
  getElectronicPdfButtonLabel,
  renderElectronicStatusTag
} from './billDetailHelpers';

const { Text } = Typography;

interface BillDetailElectronicInvoiceCardProps {
  bill: IMovementRS;
  retrying: boolean;
  onRetryEmission: () => void;
  onOpenEmailModal: () => void;
}

export const BillDetailElectronicInvoiceCard: React.FC<BillDetailElectronicInvoiceCardProps> = ({
  bill,
  retrying,
  onRetryEmission,
  onOpenEmailModal
}) => {
  const isElectronic = bill.invoice_type === 'ELECTRONIC_INVOICE' || bill.electronic_invoice_status || bill.electronic_invoice_number;
  if (!isElectronic) return null;

  return (
    <Card 
      title={
        <Space>
          <Tag color="green" style={{ margin: 0 }}>DIAN</Tag>
          <span>{getElectronicDocumentTitle(bill.document_type)}</span>
        </Space>
      } 
      className="card-shadow" 
      style={{ marginBottom: 16, borderTop: '3px solid #52c41a' }}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <div>
          <Text type="secondary">Estado ante la DIAN:</Text>
          <div style={{ marginTop: 4 }}>
            {renderElectronicStatusTag(bill.electronic_invoice_status)}
          </div>
        </div>

        {bill.electronic_invoice_number && (
          <div>
            <Text type="secondary">Número Oficial DIAN:</Text>
            <div style={{ fontWeight: 600, fontSize: 15, color: '#1890ff' }}>
              {bill.electronic_invoice_number}
            </div>
          </div>
        )}

        {bill.electronic_invoice_cufe && (
          <div>
            <Text type="secondary">{getElectronicCodeLabel(bill.document_type)}</Text>
            <div style={{ 
              fontSize: 11, 
              backgroundColor: '#f5f5f5', 
              padding: 6, 
              borderRadius: 4, 
              wordBreak: 'break-all',
              fontFamily: 'monospace',
              marginTop: 4
            }}>
              {bill.electronic_invoice_cufe}
            </div>
            <Button 
              type="link" 
              size="small" 
              icon={<CopyOutlined />} 
              style={{ padding: 0, marginTop: 4 }}
              onClick={() => {
                navigator.clipboard.writeText(bill.electronic_invoice_cufe || "");
                message.success(`${getElectronicCodeLabel(bill.document_type).replace(':', '')} copiado al portapapeles`);
              }}
            >
              {getCopyCodeButtonLabel(bill.document_type)}
            </Button>
          </div>
        )}

        {bill.electronic_invoice_validated_at && (
          <div>
            <Text type="secondary">Validada el:</Text>
            <div style={{ fontSize: 12 }}>
              {formatDateTime(bill.electronic_invoice_validated_at)}
            </div>
          </div>
        )}

        <Divider style={{ margin: '8px 0' }} />

        <Space direction="vertical" style={{ width: '100%' }}>
          {bill.electronic_invoice_public_url && (
            <Button 
              type="primary" 
              icon={<DownloadOutlined />} 
              style={{ width: '100%', backgroundColor: '#52c41a', borderColor: '#52c41a' }}
              onClick={() => window.open(bill.electronic_invoice_public_url!, '_blank')}
            >
              {getElectronicPdfButtonLabel(bill.document_type)}
            </Button>
          )}

          {bill.electronic_invoice_qr_url && (
            <Button 
              icon={<QrcodeOutlined />} 
              style={{ width: '100%' }}
              onClick={() => window.open(bill.electronic_invoice_qr_url!, '_blank')}
            >
              Verificar en Catálogo DIAN (QR)
            </Button>
          )}

          {bill.electronic_invoice_number && (
            <Button 
              icon={<MailOutlined />} 
              style={{ width: '100%' }}
              onClick={onOpenEmailModal}
            >
              Reenviar por Correo
            </Button>
          )}

          {bill.electronic_invoice_status !== 'ACCEPTED' && (
            <Button 
              icon={<ReloadOutlined />} 
              type="dashed" 
              danger 
              loading={retrying}
              style={{ width: '100%' }}
              onClick={onRetryEmission}
            >
              Reintentar Emisión ante DIAN
            </Button>
          )}
        </Space>
      </Space>
    </Card>
  );
};
